import React from 'react';
import styled from 'styled-components';
import { useMatches, useSearchParams } from 'react-router-dom';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import { DEFAULT_SORT, DEFAULT_STATUS, parseSort, parseStatus } from '../utils/api';
import { SORT_OPTIONS, STATUS_OPTIONS } from '../utils/selectors';
import {
  DEFAULT_SLUG_URL_PATTERN,
  DEFAULT_TAG_SLUGS,
  appendQueryParams,
  formatSlugUrl,
  type SlugTag,
} from '../utils/tags';

type PolyFiltersProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

type SlugItem = SlugTag & { url: string };

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const PolyFilters = (componentProps: PolyFiltersProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const uriMatches = useMatches();
  const [searchParams, setSearchParams] = useSearchParams();

  const urlPattern: string = props.properties?.urlPattern || DEFAULT_SLUG_URL_PATTERN;
  const pathParamKey: string = (props.properties?.pathParamKey || 'slugId').replace(/^:/, '');

  let activeSlug = '';
  if (uriMatches?.length) {
    const match = uriMatches[uriMatches.length - 1];
    const fromPath = match?.params?.[pathParamKey];
    if (fromPath) activeSlug = fromPath;
  }

  const sort = parseSort(searchParams.get('sort'));
  const status = parseStatus(searchParams.get('status'));
  const urlSearch = searchParams.get('search') ?? '';

  const searchDelayMs: number = Number(props.properties?.searchDelayMs) || 400;

  const [search, setSearchValue] = React.useState(urlSearch);

  React.useEffect(() => {
    setSearchValue(urlSearch);
  }, [urlSearch]);

  React.useEffect(() => {
    if (search === urlSearch) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const trimmed = search.trim();
      if (!trimmed) next.delete('search');
      else next.set('search', trimmed);
      setSearchParams(next, { replace: true });
    }, searchDelayMs);
    return () => clearTimeout(timer);
  }, [search, urlSearch, searchParams, setSearchParams, searchDelayMs]);

  const slugs: SlugItem[] = React.useMemo(() => {
    const query = {
      sort: sort !== DEFAULT_SORT ? sort : undefined,
      status: status !== DEFAULT_STATUS ? status : undefined,
      search: search || undefined,
    };
    return DEFAULT_TAG_SLUGS.map((t) => ({
      ...t,
      url: appendQueryParams(formatSlugUrl(urlPattern, t.slug), query),
    }));
  }, [urlPattern, sort, status, search]);

  const setParam = React.useCallback(
    (key: string, value: string, defaultValue: string) => {
      const next = new URLSearchParams(searchParams);
      if (value === defaultValue) next.delete(key);
      else next.set(key, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setSearch = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const sortOptions = React.useMemo(
    () =>
      SORT_OPTIONS.map((o) => ({
        ...o,
        isActive: o.value === sort,
        onSelect: () => setParam('sort', o.value, DEFAULT_SORT),
      })),
    [sort, setParam],
  );

  const statusOptions = React.useMemo(
    () =>
      STATUS_OPTIONS.map((o) => ({
        ...o,
        isActive: o.value === status,
        onSelect: () => setParam('status', o.value, DEFAULT_STATUS),
      })),
    [status, setParam],
  );

  const sortLabel = sortOptions.find((o) => o.isActive)?.label ?? '';
  const statusLabel = statusOptions.find((o) => o.isActive)?.label ?? '';

  const contextValue = React.useMemo(
    () => ({
      slugs,
      activeSlug,
      sort,
      sortLabel,
      status,
      statusLabel,
      search,
      setSearch,
      sortOptions,
      statusOptions,
      parentContext: dataElementContext,
    }),
    [
      slugs,
      activeSlug,
      sort,
      sortLabel,
      status,
      statusLabel,
      search,
      setSearch,
      sortOptions,
      statusOptions,
      dataElementContext,
    ],
  );

  if (!isVisible) return null;

  console.log('PolyFilters[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default PolyFilters;
